import React, { memo } from 'react';
import { renderToString } from 'react-dom/server';
import PropTypes from 'prop-types';
import Editor from 'react-simple-code-editor';
import { Chip } from 'uikit/chips';
import Prism from 'prismjs/components/prism-core';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-python';

import 'prismjs/themes/prism.css';

import { registerField } from 'core/forms';

import styles from '../styles/formFieldCodeEditor.module.scss';

Prism.languages.jinja2 = Prism.languages.extend('markup', {});

// 2. Comments: {# … #}
Prism.languages.insertBefore('python', 'comment', {
  'jinja2-comment': {
    pattern: /\{#[\s\S]*?#\}/,
    greedy: true,
    alias: 'comment'
  }
});

// 3. Blocks: {% … %}
Prism.languages.insertBefore('jinja2', 'tag', {
  'jinja2-block': {
    pattern: /\{%\s*[\s\S]*?\s*%\}/,
    greedy: true,
    inside: {
      'delimiter': {
        pattern: /^\{%-?|-?%\}$/,
        alias: 'punctuation'
      },
      'keyword': {
        pattern: /(\{%\s*)(?:if|else|elif|endif|for|endfor|block|endblock|extends|include|import|from|with|endwith|set|endset)\b/,
        lookbehind: true
      },
      'string': {
        pattern: /(["'])(?:\\.|(?!\1)[^\\\r\n])*\1/,
        greedy: true
      },
      'number': /\b\d+(?:\.\d+)?\b/,
      'operator': /[+\-*/%=<>!]+/,
      'punctuation': /[()[\],.:]/
    }
  },

  // 4. Variables: {{ … }}
  'jinja2-variable': {
    pattern: /\{\{\s*[\s\S]*?\s*\}\}/,
    greedy: true,
    inside: {
      'delimiter': {
        pattern: /^\{\{\s*|\s*\}\}$/,
        alias: 'punctuation'
      },
      'string': {
        pattern: /(["'])(?:\\.|(?!\1)[^\\\r\n])*\1/,
        greedy: true
      },
      'number': /\b\d+(?:\.\d+)?\b/,
      'variable': /\b[a-zA-Z_]\w*\b/,
      'operator': /[+\-*/%=<>!]+/,
      'punctuation': /[()[\],.:]/
    }
  }
});

// 5. Ensure our tokens get the proper CSS class
Prism.hooks.add('wrap', (env) => {
  if (/^jinja2-/.test(env.type)) {
    env.classes.push('language-jinja2');
  }
});

// (string => string | React.Node): Callback which will receive text to highlight. You'll need to return an HTML string or a React element with syntax highlighting using a library such as prismjs.
const handleHighlight = (code, lang = 'js') => {
  // if (lang === 'jinja2') {
  //   return code.replace(/\{\{[^}]*\}\}/g, (match) => `<strong>${match}</strong>`);
  // }

  if (lang === 'ai_prompt') {
    return code.replace(
      /\{ *([^}{]*) *\}/g,
      (match, group1) => renderToString(<Chip className={styles.chip} text={group1} variant="outlined" />),
    );
  }

  return Prism.highlight(code, Prism.languages[lang]);
};

const FormFieldCodeEditor = ({
  fieldId,
  schema = {
    className: '',
    fontFamily: '"Fira code", "Fira Mono", monospace',
    fontSize: 12,
    isDisabled: false,
    lang: 'js',
    maxLength: null,
    placeholder: '',
  },
  updateField,
  value = '',
}) => (
  <div className={styles.element}>
    <Editor
      id={fieldId}
      value={value === null ? '' : value}
      onValueChange={updateField}
      placeholder={schema.placeholder}
      highlight={(code) => handleHighlight(code, schema.lang)}
      disabled={schema.isDisabled}
      maxLength={schema.maxLength}
      tabSize={2}
      padding={10}
      style={{
        fontFamily: schema.fontFamily || '"Fira code", "Fira Mono", monospace',
        fontSize: schema.fontSize || 12,
        minHeight: '12.5rem',
        resize: 'vertical',
      }}
    />
  </div>
);

FormFieldCodeEditor.propTypes = {
  fieldId: PropTypes.string.isRequired,
  value: PropTypes.string,
  updateField: PropTypes.func.isRequired,
  schema: PropTypes.shape({
    placeholder: PropTypes.string,
    isDisabled: PropTypes.bool,
    lang: PropTypes.string,
    maxLength: PropTypes.number,
  }),
};

registerField('CodeEditor', memo(FormFieldCodeEditor));

export default FormFieldCodeEditor;
